import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  type AdminLoginRequest,
  type AutoLoginRequest,
  type StandardLoginRequest,
  authenticationApiService,
} from "./authenticationApi.service.js";
export type OnBoardingStatus = "FEATURES" | "FEATURES2" | "HELP" | "ENDING" | "FINISHED";

export const authKeysStore = {
  root: ["authentication"] as const,
  authenticate: () => [...authKeysStore.root, "authenticate"] as const,
  moderation: () => [...authKeysStore.root, "moderation"] as const,
};

export function useIsAuthenticated() {
  const {data, isLoading, refetch} = useQuery({
    queryKey: authKeysStore.authenticate(),
    queryFn: () => authenticationApiService.isAuthenticated(),
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY, // Authentication status doesn't change often
  });

  return {
    isAuthenticated: !!data,
    isAuthenticatedLoading: isLoading,
    refetchAuthentication: refetch,
  };
}

export function useModeration() {
  const {data, isLoading, refetch} = useQuery({
    queryKey: authKeysStore.moderation(),
    queryFn: () => authenticationApiService.isModeration(),
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY, // Moderation status doesn't change often
  });

  return {
    isModeration: !!data,
    isModerationLoading: isLoading,
    refetchModeration: refetch,
  };
}

export function useStandardLogin() {
  const queryClient = useQueryClient();

  const {mutateAsync, isPending, error, isSuccess} = useMutation({
    async mutationFn(request: StandardLoginRequest) {
      await authenticationApiService.standardLogin(request);
      await queryClient.invalidateQueries({queryKey: authKeysStore.root});
    },
  });

  return {
    login: mutateAsync,
    isLoggingIn: isPending,
    loginError: error,
    loginSuccess: isSuccess,
  };
}

export function useAdminLogin() {
  const queryClient = useQueryClient();

  const {mutateAsync, isPending, error, isSuccess} = useMutation({
    async mutationFn(request: AdminLoginRequest) {
      await authenticationApiService.adminLogin(request);
      await queryClient.invalidateQueries({queryKey: authKeysStore.root});
    },
  });

  return {
    adminLogin: mutateAsync,
    isAdminLoginPending: isPending,
    adminLoginError: error,
    adminLoginSuccess: isSuccess,
  };
}

export function useAutoLogin() {
  const queryClient = useQueryClient();

  const {mutateAsync, isPending, error, isSuccess} = useMutation({
    async mutationFn(request: AutoLoginRequest) {
      await authenticationApiService.autoLogin(request);
      await queryClient.invalidateQueries({queryKey: authKeysStore.root});
    },
  });

  return {
    autoLogin: mutateAsync,
    isAutoLoginPending: isPending,
    autoLoginError: error,
    onboardingCompleted: isSuccess,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();

  const {mutateAsync, isPending} = useMutation({
    async mutationFn() {
      authenticationApiService.logout();
      queryClient.clear();
      return Promise.resolve();
    },
  });

  return {
    logout: mutateAsync,
    isLoggingOut: isPending,
  };
}
